import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Menu } from 'lucide-react';
import '../App.css';
import './Navbar.css';
import navbarCling from '../assets/images/navbar-cling.webp';
import { openAppStoreWithOpenAIAds } from '../lib/openaiAds';
import AppStoreQrHover from './AppStoreQrHover';

const Navbar = ({ ctaSource = 'navbar_try_for_free' }: { ctaSource?: string }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuButton = useRef<HTMLButtonElement>(null);
  const homeLink = useRef<HTMLAnchorElement>(null);
  const downloadButton = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!menuOpen) return;

    const mobileViewport = window.matchMedia('(max-width: 991px)');
    const closeOnDesktop = () => {
      if (!mobileViewport.matches) setMenuOpen(false);
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    mobileViewport.addEventListener('change', closeOnDesktop);

    return () => {
      document.body.style.overflow = previousOverflow;
      mobileViewport.removeEventListener('change', closeOnDesktop);
    };
  }, [menuOpen]);

  const handleMenuKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (!menuOpen) return;

    if (event.key === 'Escape') {
      setMenuOpen(false);
      menuButton.current?.focus();
    } else if (event.key === 'Tab') {
      const edge = event.shiftKey ? homeLink : downloadButton;
      const next = event.shiftKey ? downloadButton : homeLink;
      if (event.target === edge.current) {
        event.preventDefault();
        next.current?.focus();
      }
    }
  };

  const closeMenu = () => setMenuOpen(false);

  return (
    <nav aria-label="Main navigation" className={`site-nav${menuOpen ? ' site-nav--open' : ''}`} onKeyDown={handleMenuKeyDown}>
      <div className="site-nav__row">
        <Link to="/" ref={homeLink} className="site-nav__brand" onClick={closeMenu}>
          <img className="site-nav__cling" src={navbarCling} alt="" width={50} height={50} />
          <span className="site-nav__wordmark">uncling</span>
        </Link>
        <button
          type="button"
          ref={menuButton}
          className="site-nav__toggle"
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={menuOpen}
          aria-controls="site-nav-menu"
          onClick={() => setMenuOpen(!menuOpen)}
        >
          <Menu size={20} aria-hidden="true" />
        </button>
        <div id="site-nav-menu" className="site-nav__menu">
          <div className="site-nav__links">
            <Link to="/attachment-style-quiz" onClick={closeMenu}>Quiz</Link>
            <Link to="/blog" onClick={closeMenu}>Blog</Link>
            <Link to="/careers" onClick={closeMenu}>Careers</Link>
            <Link to="/about" onClick={closeMenu}>About Us</Link>
            <Link to="/contact" onClick={closeMenu}>Support</Link>
          </div>
          <AppStoreQrHover placement="bottom">
            <button
              type="button"
              ref={downloadButton}
              className="site-nav__cta"
              onClick={() => openAppStoreWithOpenAIAds(ctaSource)}
            >
              Try For Free
            </button>
          </AppStoreQrHover>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
